import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { bousalaProductBySlug } from "@/lib/bousala/products";
import { siteOrigin } from "@/lib/site-origin.server";
import { activationLink, traumaActivationDestination } from "@/lib/trauma/activation";
import { purchasePaymentMethodForCountry } from "@/lib/payments/outside-syria";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
}

// Granting a paid product is an admin action. This must match
// `approveBousalaPurchase` in src/lib/bousala-purchase.functions.ts, which is
// already admin-only. Two screens must not have two different privilege
// levels for the same outcome.
async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden");
}

export const listAllPendingPurchases = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin as any)
      .from("pending_purchases")
      .select(
        "id, product_id, status, receipt_file, amount_cents, currency, environment, reason, email, full_name, phone, country, created_at, reviewed_at, wa_notified_at",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    const rows = (data ?? []) as any[];
    if (!rows.length) return [];
    const productIds = Array.from(new Set(rows.map((r) => r.product_id).filter(Boolean)));
    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, slug, title_ar, type")
      .in("id", productIds);
    const pMap = new Map((products ?? []).map((p: any) => [p.id, p]));
    return rows.map((r) => ({ ...r, product: pMap.get(r.product_id) ?? null }));
  });

export const getPendingReceiptSignedUrl = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ path: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await supabaseAdmin.storage
      .from("payment-receipts")
      .createSignedUrl(data.path, 60 * 10);
    if (error) throw error;
    return { url: signed.signedUrl };
  });

// Approve a generic pending_purchases row (any product type).
export const approvePendingPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ pendingId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    // Admin-only: granting a paid product is an admin action.
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: p, error } = await (supabaseAdmin as any)
      .from("pending_purchases")
      .select("*")
      .eq("id", data.pendingId)
      .single();
    if (error) throw error;
    if (p.status !== "pending") throw new Error("هذا الطلب معالج مسبقًا.");

    // Product metadata for redirect target after magic link
    const { data: product } = await supabaseAdmin
      .from("products")
      .select("id, slug, type, title_ar")
      .eq("id", p.product_id)
      .single();
    if (!product) throw new Error("Product not found");

    const email = String(p.email).trim().toLowerCase();

    // Find/create the auth user (deferred until approval). An existing
    // account keeps its contact details and gets no sign-in link; staff
    // accounts are refused so the admin handles them by hand.
    const acct = await import("@/lib/approval-account.server");
    const { userId, existing } = await acct.resolveApprovalAccount(supabaseAdmin, email, p.full_name);
    const profile = {
      id: await acct.profileForApproval(supabaseAdmin, userId, existing, {
        full_name: p.full_name,
        phone_whatsapp: p.phone,
        country: p.country ?? null,
      }),
    };

    const { data: purchase, error: purErr } = await supabaseAdmin
      .from("purchases")
      .insert({
        client_id: profile.id,
        product_id: p.product_id,
        status: "granted",
        payment_method: purchasePaymentMethodForCountry(p.country),
        receipt_file: p.receipt_file,
        amount_cents: p.amount_cents,
        currency: p.currency ?? "USD",
        tier: "full",
        environment: p.environment ?? "sandbox",
        granted_at: new Date().toISOString(),
      })
      .select("id")
      .single();
    if (purErr) throw purErr;

    // A Smart Bousala receipt is approved here too, on the generic
    // pending-purchases screen, not only on the Bousala-specific one. Without
    // this call, approving a Smart receipt created the purchase but never the
    // order row — the buyer paid and never entered the generation queue.
    // The helper no-ops for any non-bousala-smart product.
    try {
      const { ensureSmartOrderForPurchase } = await import(
        "@/lib/bousala/smart-order.server"
      );
      await ensureSmartOrderForPurchase(supabaseAdmin, purchase.id);
    } catch (err) {
      console.error("[bousala] ensureSmartOrderForPurchase failed", {
        purchaseId: purchase.id,
        error: err instanceof Error ? err.message : String(err),
      });

      // Do not throw — access has already been granted.
    }

    await (supabaseAdmin as any)
      .from("pending_purchases")
      .update({
        status: "approved",
        reviewed_by: context.userId,
        reviewed_at: new Date().toISOString(),
        approved_purchase_id: purchase.id,
        wa_notified_at: new Date().toISOString(),
      })
      .eq("id", data.pendingId);

    if (existing) return acct.existingAccountResult(p.phone, p.full_name, email);
    return buildActivationMessage(supabaseAdmin, p, product, email);
  });

/** Fresh single-use magic link + WhatsApp activation message. Shared by approve and resend. */
export async function buildActivationMessage(
  supabaseAdmin: any,
  p: { full_name: string | null; phone: string | null },
  product: { slug: string; type: string; title_ar?: string | null },
  email: string,
) {
  const origin = siteOrigin();
  // trauma-* only: link to our own /activate page (token in the fragment) so a
  // WhatsApp link preview cannot use up the one-time token.
  const traumaDest = traumaActivationDestination(product.slug);
  const redirectPath =
    traumaDest ??
    // Courses were retired; buyers of any legacy non-test product land in the portal.
    (product.type === "course"
      ? "/portal"
      : bousalaProductBySlug(product.slug)
        ? "/bousala/play"
        : `/tests/${product.slug}/play`);
  const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
    type: "magiclink",
    email,
    options: { redirectTo: `${origin}${redirectPath}` },
  });
  if (linkErr) throw linkErr;

  if (traumaDest) {
    const hashed: string = linkData?.properties?.hashed_token ?? "";
    const link = activationLink(origin, hashed, traumaDest);
    const message =
      `مرحبًا ${p.full_name ?? ""}\n` +
      `تم تأكيد دفعتك وتفعيل «${product.title_ar ?? ""}». بعد فتح الرابط، الضغط على «الدخول إلى دوراتي»:\n${link}\n\n` +
      `الرابط صالح لمدة محدودة — إذا انتهت صلاحيته، راسلونا لإعادة الإرسال.`;
    return { magicLink: link, waLink: toWhatsAppLink(p.phone, message) };
  }

  const magicLink: string = linkData?.properties?.action_link ?? "";

  const message =
    `مرحبًا ${p.full_name ?? ""}\n` +
    `تم تفعيل حسابك. البداية من هذا الرابط:\n${magicLink}\n\n` +
    `الرابط صالح لمدة محدودة — إذا انتهت صلاحيته، راسلونا لإعادة الإرسال.`;
  const waLink = toWhatsAppLink(p.phone, message);
  return { magicLink, waLink };
}

export const resendActivationLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ pendingId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: p, error } = await (supabaseAdmin as any)
      .from("pending_purchases")
      .select("id, status, email, full_name, phone, product_id, created_at")
      .eq("id", data.pendingId)
      .single();
    if (error) throw error;
    if (p.status !== "approved") throw new Error("يمكن إعادة الإرسال للطلبات المفعّلة فقط.");
    const { data: product } = await supabaseAdmin
      .from("products").select("slug, type, title_ar").eq("id", p.product_id).single();
    if (!product) throw new Error("Product not found");
    const email = String(p.email).trim().toLowerCase();
    // Same rule as approval: an account that existed before this receipt was
    // submitted gets no sign-in link, and staff accounts are refused.
    const acct = await import("@/lib/approval-account.server");
    const { findAuthUserByEmail } = await import("@/lib/find-auth-user.server");
    const matched = await findAuthUserByEmail(supabaseAdmin, email);
    if (!matched) throw new Error("لا يوجد حساب لهذا البريد.");
    await acct.assertNotStaffAccount(supabaseAdmin, matched.id);
    const { data: u } = await supabaseAdmin.auth.admin.getUserById(matched.id);
    const createdAt = u?.user?.created_at ? Date.parse(u.user.created_at) : NaN;
    const preExisting = !(createdAt >= Date.parse(p.created_at));
    const result = preExisting
      ? acct.existingAccountResult(p.phone, p.full_name, email)
      : await buildActivationMessage(supabaseAdmin, p, product, email);
    await (supabaseAdmin as any)
      .from("pending_purchases")
      .update({ wa_notified_at: new Date().toISOString() })
      .eq("id", data.pendingId);
    return result;
  });

export const rejectPendingPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ pendingId: z.string().uuid(), reason: z.string().min(2).max(500) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertStaff(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: p, error } = await (supabaseAdmin as any)
      .from("pending_purchases")
      .select("id, email, full_name, phone, status")
      .eq("id", data.pendingId)
      .single();
    if (error) throw error;
    if (p.status !== "pending") throw new Error("هذا الطلب معالج مسبقًا.");

    await (supabaseAdmin as any)
      .from("pending_purchases")
      .update({
        status: "rejected",
        reason: data.reason,
        reviewed_by: context.userId,
        reviewed_at: new Date().toISOString(),
        wa_notified_at: new Date().toISOString(),
      })
      .eq("id", data.pendingId);

    const message =
      `مرحبًا ${p.full_name ?? ""}\n` +
      `شكرًا لتواصلك مع د. سارة. للأسف تعذّر تأكيد إيصال الدفع للسبب التالي:\n${data.reason}\n\n` +
      `يسعدنا مساعدتك — راسلونا لإعادة المحاولة.`;
    const waLink = toWhatsAppLink(p.phone, message);

    return { ok: true, waLink };
  });